import { keepPreviousData } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Activity,
  AlertTriangle,
  BriefcaseMedical,
  Calendar,
  CheckCircle,
  Clock,
  Eye,
  FileText,
  FileWarning,
  Hash,
  MessageSquare,
  MoreHorizontal,
  Stethoscope,
  User,
  UserCheck,
  UserPlus,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  type GetAllReservations,
  getAllReservationsSchema,
} from '@virtual-care/client';
import {
  displayName,
  formatDateTimeShort,
  formatReservationDate,
  formatReservationDateTime,
  getPatientDemographics,
} from '@virtual-care/isomorphic';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  cn,
} from '@virtual-care/shadcn';
import { downloadFile } from '@virtual-care/media-web';
import {
  RESERVATION_STATUS_META,
  ReservationStatus,
  SearchInput,
  TableRowSort,
  useData,
} from '@virtual-care/ui';

import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';
import { RatingBadge, RatingsDialog } from './appointment-review.tsx';
import { AssignDoctorDialog } from './assign-doctor-dialog.tsx';
import { AttachedFiles } from './attached-files.tsx';
import { PatientInfo } from './patient-info.tsx';
import { ReassignDoctorDialog } from './reassign-doctor-dialog.tsx';

type ReservationRecord = GetAllReservations['records'][number];

const wasReferred = (reservation: ReservationRecord) =>
  reservation.timeline?.some((e) => e.type === 'REFERRED_TO_ANOTHER_DOCTOR') ??
  false;

const statusLabel = (status: string) =>
  RESERVATION_STATUS_META[status as keyof typeof RESERVATION_STATUS_META]?.en ??
  status;

const StatusEnum = getAllReservationsSchema.shape.status;

export default function AppointmentsPage() {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'scheduledDate', desc: false },
  ]);
  const [searchQuery, setSearchQuery] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<
    z.infer<typeof getAllReservationsSchema.shape.status> | undefined
  >();

  const [selectedReservation, setSelectedReservation] =
    useState<ReservationRecord | null>(null);
  const [showCaseDetails, setShowCaseDetails] = useState(false);
  const [showAssignDoctor, setShowAssignDoctor] = useState(false);
  const [showReassignDoctor, setShowReassignDoctor] = useState(false);
  const [showRatings, setShowRatings] = useState(false);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const dataQuery = useData(
    'GET /reservations',
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      status: statusFilter,
      query: searchQuery || undefined,
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  const handleAssignDoctor = (reservation: ReservationRecord) => {
    setSelectedReservation(reservation);
    setShowAssignDoctor(true);
  };

  const handleReassignDoctor = (reservation: ReservationRecord) => {
    setSelectedReservation(reservation);
    setShowReassignDoctor(true);
  };

  const columns: ColumnDef<ReservationRecord>[] = [
    {
      accessorKey: 'reservationNumber',
      enableSorting: false,
      header: () => {
        return 'Case #';
      },
      cell: ({ row }) => (
        <div className="font-mono font-medium">
          #{row.getValue('reservationNumber')}
        </div>
      ),
    },
    {
      id: 'patientName',
      enableSorting: false,
      header: 'Patient Name',
      cell: ({ row }) => {
        return (
          <div className="text-sm font-medium">
            {displayName(row.original.user)}
          </div>
        );
      },
    },
    {
      id: 'doctorName',
      accessorFn: (row) => {
        if (!row.doctor || !row.doctorId) return '';
        return `${row.doctor.firstName} ${row.doctor.lastName}`;
      },
      enableSorting: false,
      header: 'Assigned Doctor',
      cell: ({ row }) => {
        const doctor = row.original.doctor;
        const doctorId = row.original.doctorId;
        return doctor && doctorId ? (
          <div className="text-sm">
            {doctor.firstName} {doctor.lastName}
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">Not assigned</span>
        );
      },
    },
    {
      id: 'speciality',
      accessorKey: 'speciality.name',
      enableSorting: false,
      header: 'Speciality',
      cell: ({ row }) => (
        <div className="font-medium">{row.original.speciality.name}</div>
      ),
    },
    {
      accessorKey: 'status',
      enableSorting: false,
      header: 'Status',
      cell: ({ row }) => {
        const reservation = row.original;

        return (
          <div className="flex items-center gap-2">
            <ReservationStatus.Root status={reservation.status} />
          </div>
        );
      },
    },
    {
      id: 'ratings',
      enableSorting: false,
      header: 'Ratings',
      cell: ({ row }) => {
        const summary = row.original.ratingSummary;
        if (!summary) {
          return (
            <span className="text-muted-foreground text-xs">No reviews</span>
          );
        }
        return (
          <button
            onClick={() => {
              setSelectedReservation(row.original);
              setShowRatings(true);
            }}
            className="hover:bg-muted flex items-center gap-1.5 rounded-md px-2 py-1 transition-colors"
          >
            <RatingBadge summary={summary} />
          </button>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      enableSorting: false,
      header: () => {
        return 'Created';
      },
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-2">
            <div className="text-sm">
              {formatDateTimeShort(row.original.createdAt)}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'scheduledDate',
      enableSorting: false,
      header: 'Scheduled Date',
      cell: ({ row }) => {
        const isCompletedState = ['FINISHED', 'CANCELED'].includes(
          row.original.status,
        );
        return (
          <div
            className={cn(
              'text-sm',
              isCompletedState && 'text-muted-foreground line-through',
            )}
          >
            {formatReservationDateTime(row.original)}
            {isCompletedState && (
              <div className="text-xs text-red-600 dark:text-red-400">
                {statusLabel(row.original.status)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => {
        const reservation = row.original;
        const hasDoctor = !!reservation.doctorId;
        const canAssignDoctor =
          !hasDoctor &&
          !['FINISHED', 'CANCELED', 'EXPIRED'].includes(reservation.status);

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Case Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />

              <DropdownMenuItem
                onClick={() => {
                  setSelectedReservation(reservation);
                  setShowCaseDetails(true);
                }}
              >
                <Eye className="mr-2 h-4 w-4" />
                View case details
              </DropdownMenuItem>

              {reservation.totalSickLeaves > 0 && (
                <DropdownMenuItem
                  onClick={async () => {
                    try {
                      await downloadFile(
                        'GET /reservations/{id}/sick-leave.pdf',
                        { id: reservation.id },
                        `sick-leave-certificate-${reservation.reservationNumber}.pdf`,
                      );
                    } catch {
                      toast.error('Failed to download sick leave certificate');
                    }
                  }}
                >
                  <FileWarning className="mr-2 h-4 w-4" />
                  View sick leave certificate
                </DropdownMenuItem>
              )}

              {canAssignDoctor && !wasReferred(reservation) && (
                <DropdownMenuItem
                  onClick={() => handleAssignDoctor(reservation)}
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Assign doctor
                </DropdownMenuItem>
              )}

              {wasReferred(reservation) && !hasDoctor && canAssignDoctor && (
                <DropdownMenuItem
                  onClick={() => handleReassignDoctor(reservation)}
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Assign to new doctor
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigator.clipboard.writeText(reservation.id)}
                className="text-muted-foreground"
              >
                Copy case ID
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data: dataQuery.data?.records ?? [],
    columns,
    manualPagination: true,
    manualSorting: true,
    rowCount: dataQuery.data?.totalCount,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    state: {
      sorting,
      pagination,
    },
  });

  return (
    <div className="w-full">
      <CountablePageHeader
        title="Appointments"
        totalCount={`${dataQuery.data?.totalCount} total appointments`}
        icon={BriefcaseMedical}
      />

      {dataQuery.data && (
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="bg-card border-border rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <Clock className="size-4 text-yellow-600 dark:text-yellow-400" />
              <div>
                <div className="text-muted-foreground text-sm">Waiting</div>
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {
                    dataQuery.data.records.filter(
                      (r) => r.status === 'WAITING_DOCTOR_ASSIGNMENT',
                    ).length
                  }
                </div>
              </div>
            </div>
          </div>
          <div className="bg-card border-border rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="size-4 text-green-600 dark:text-green-400" />
              <div>
                <div className="text-muted-foreground text-sm">Confirmed</div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {
                    dataQuery.data.records.filter(
                      (r) => r.status === 'CONFIRMED',
                    ).length
                  }
                </div>
              </div>
            </div>
          </div>
          <div className="bg-card border-border rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <User className="size-4 text-blue-600 dark:text-blue-400" />
              <div>
                <div className="text-muted-foreground text-sm">Unassigned</div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {dataQuery.data.records.filter((r) => !r.doctorId).length}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 flex items-center gap-4">
        <div className="flex-1">
          <SearchInput
            placeholder="Search appointments by patient name, appointment number, or description..."
            value={searchQuery}
            onChange={setSearchQuery}
            onClear={() => setSearchQuery('')}
            isLoading={dataQuery.isFetching}
          />
        </div>
        <Select
          value={statusFilter ?? 'all'}
          onValueChange={(v) =>
            setStatusFilter(v === 'all' ? undefined : StatusEnum.parse(v))
          }
        >
          <SelectTrigger className="min-h-11">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="WAITING_DOCTOR_ASSIGNMENT">
              {statusLabel('WAITING_DOCTOR_ASSIGNMENT')}
            </SelectItem>
            <SelectItem value="WAITING_DOCTOR_CONFIRMATION">
              {statusLabel('WAITING_DOCTOR_CONFIRMATION')}
            </SelectItem>
            <SelectItem value="FINISHED">{statusLabel('FINISHED')}</SelectItem>
            <SelectItem value="CONFIRMED">
              {statusLabel('CONFIRMED')}
            </SelectItem>
            <SelectItem value="CANCELED">{statusLabel('CANCELED')}</SelectItem>

            <SelectItem value="AVAILABLE_FOR_FOLLOWUP">
              {statusLabel('AVAILABLE_FOR_FOLLOWUP')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRowSort key={headerGroup.id} headerGroup={headerGroup} />
            ))}
          </TableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading appointments...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No appointments found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />

      {selectedReservation && (
        <>
          <AppointmentDetailsDialog
            selectedReservation={selectedReservation}
            open={showCaseDetails}
            onOpenChange={setShowCaseDetails}
          />

          <AssignDoctorDialog
            selectedReservation={selectedReservation}
            open={showAssignDoctor}
            onOpenChange={setShowAssignDoctor}
            onSuccess={() => void dataQuery.refetch()}
          />

          <ReassignDoctorDialog
            selectedReservation={selectedReservation}
            open={showReassignDoctor}
            onOpenChange={setShowReassignDoctor}
            onSuccess={() => void dataQuery.refetch()}
          />

          <RatingsDialog
            selectedReservation={selectedReservation}
            open={showRatings}
            onOpenChange={setShowRatings}
          />
        </>
      )}
    </div>
  );
}

// Dialog Components
interface AppointmentDetailsDialogProps {
  selectedReservation: ReservationRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const timelineTypeLabels = {
  // Core reservation events
  RESERVATION_CREATED: 'Reservation Created',
  RESERVATION_CANCELED: 'Reservation Canceled',
  RESERVATION_FINISHED: 'Reservation Finished',

  // Doctor assignment actions
  DOCTOR_ASSIGNED: 'Doctor Assigned',

  // Appointment confirmation flow
  APPOINTMENT_CONFIRMED_BY_DOCTOR: 'Appointment Confirmed by Doctor',
  APPOINTMENT_WAITING_FOR_DOCTOR_CONFIRMATION:
    'Waiting for Doctor Confirmation',

  // Communication actions
  INFO_SUBMITTED: 'Additional Info Submitted',
  MORE_INFO_NEEDED: 'More Info Needed',

  // Referral actions
  REFERRED_TO_ANOTHER_DOCTOR: 'Referred to Another Doctor',

  // Follow-up actions
  MARKED_AVAILABLE_FOR_FOLLOWUP: 'Marked Available for Follow-up',
  FOLLOWUP_WINDOW_EXPIRED: 'Follow-up Window Expired',

  // Call participation events
  PATIENT_JOINED_CALL: 'Patient Joined Call',
  DOCTOR_JOINED_CALL: 'Doctor Joined Call',
};

const timelineTypeIcons = {
  // Core reservation events
  RESERVATION_CREATED: Calendar,
  RESERVATION_CANCELED: AlertTriangle,
  RESERVATION_FINISHED: CheckCircle,

  // Doctor assignment actions
  DOCTOR_ASSIGNED: Stethoscope,

  // Appointment confirmation flow
  APPOINTMENT_CONFIRMED_BY_DOCTOR: CheckCircle,
  APPOINTMENT_WAITING_FOR_DOCTOR_CONFIRMATION: Clock,

  // Communication actions
  INFO_SUBMITTED: FileText,
  MORE_INFO_NEEDED: MessageSquare,

  // Referral actions
  REFERRED_TO_ANOTHER_DOCTOR: User,

  // Follow-up actions
  MARKED_AVAILABLE_FOR_FOLLOWUP: Activity,
  FOLLOWUP_WINDOW_EXPIRED: Clock,

  // Call participation events
  PATIENT_JOINED_CALL: UserCheck,
  DOCTOR_JOINED_CALL: UserCheck,
};

const formatTimelineContext = (
  reservation: ReservationRecord,
  type: string,
  context: string | null,
) => {
  if (!context) return null;

  try {
    const parsed = JSON.parse(context);
    switch (type) {
      case 'RESERVATION_CREATED':
        return `Created by ${parsed.patientDemographics?.name ?? parsed.patient?.b2cProfile?.firstName ?? 'patient'}`;
      case 'DOCTOR_ASSIGNED':
        return `Assigned doctor and scheduled for ${formatReservationDate(reservation)}`;
      case 'APPOINTMENT_CONFIRMED_BY_DOCTOR':
        return `Doctor confirmed the appointment`;
      case 'APPOINTMENT_WAITING_FOR_DOCTOR_CONFIRMATION':
        return `Appointment assigned, waiting for doctor confirmation`;
      case 'RESERVATION_CANCELED':
        return `Reservation was canceled`;
      case 'RESERVATION_FINISHED':
        return `Reservation was completed`;
      case 'REFERRED_TO_ANOTHER_DOCTOR':
        return `Referred to another doctor for specialized care`;
      case 'MARKED_AVAILABLE_FOR_FOLLOWUP':
        return `Marked as available for follow-up appointments`;
      case 'FOLLOWUP_WINDOW_EXPIRED':
        return `Follow-up window has expired`;
      case 'PATIENT_JOINED_CALL':
        return `Patient joined the video call`;
      case 'DOCTOR_JOINED_CALL':
        return `Doctor joined the video call`;
      default:
        return context;
    }
  } catch {
    return context;
  }
};

function AppointmentDetailsDialog({
  selectedReservation,
  open,
  onOpenChange,
}: AppointmentDetailsDialogProps) {
  const patientDemographics = getPatientDemographics(
    selectedReservation.timeline,
    'RESERVATION_CREATED',
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-7xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hash className="h-5 w-5" />
            Case #{selectedReservation.reservationNumber}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <PatientInfo
              patient={selectedReservation.user}
              patientDemographics={patientDemographics}
            />

            <div className="bg-muted/50 rounded-lg p-4">
              <h3 className="mb-3 flex items-center gap-2 font-semibold">
                <BriefcaseMedical className="h-4 w-4" />
                Case Information
              </h3>
              <AppointmentInfo reservation={selectedReservation} />
            </div>

            <div className="bg-muted/50 rounded-lg p-4">
              <h3 className="mb-3 flex items-center gap-2 font-semibold">
                <MessageSquare className="h-4 w-4" />
                Description
              </h3>
              <p className="text-muted-foreground warp-break-words text-sm whitespace-pre-wrap">
                {selectedReservation.description}
              </p>
            </div>

            {selectedReservation.files.length > 0 && (
              <AttachedFiles reservation={selectedReservation} />
            )}
          </div>

          <AppointmentTimeline reservation={selectedReservation} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AppointmentStatus({
  reservation,
}: {
  reservation: ReservationRecord;
}) {
  const isCompletedState = ['FINISHED', 'CANCELED'].includes(
    reservation.status,
  );
  return (
    <div className="flex items-center gap-2">
      <Clock className="text-muted-foreground h-4 w-4" />
      <span className="text-sm font-medium">Scheduled:</span>
      <span
        className={cn(
          'text-sm',
          isCompletedState && 'text-muted-foreground line-through',
        )}
      >
        {formatReservationDateTime(reservation)}
      </span>
      {isCompletedState && (
        <span className="text-xs text-red-600 dark:text-red-400">
          {statusLabel(reservation.status)}
        </span>
      )}
    </div>
  );
}

function AppointmentTimeline({
  reservation,
}: {
  reservation: ReservationRecord;
}) {
  return (
    <div className="space-y-4">
      <div className="bg-muted/50 rounded-lg p-4">
        <h3 className="mb-3 flex items-center gap-2 font-semibold">
          <Clock className="h-4 w-4" />
          Case Timeline
        </h3>
        <div className="max-h-96 space-y-3 overflow-y-auto">
          {reservation.timeline?.length > 0 ? (
            reservation.timeline.map((entry) => {
              const IconComponent =
                timelineTypeIcons[
                  entry.type as keyof typeof timelineTypeIcons
                ] || Activity;
              return (
                <div
                  key={entry.id}
                  className="border-border flex gap-3 border-b pb-3 last:border-b-0"
                >
                  <div className="flex-shrink-0">
                    <div className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-full">
                      <IconComponent className="text-primary h-4 w-4" />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="text-sm font-medium">
                        {timelineTypeLabels[
                          entry.type as keyof typeof timelineTypeLabels
                        ] || entry.type}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {formatDateTimeShort(entry.createdAt)}
                      </span>
                    </div>
                    {entry.context && (
                      <p className="text-muted-foreground text-xs">
                        {formatTimelineContext(
                          reservation,
                          entry.type,
                          entry.context,
                        )}
                      </p>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-muted-foreground py-8 text-center">
              <Activity className="mx-auto mb-2 h-8 w-8 opacity-50" />
              <p className="text-sm">No timeline entries yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AppointmentInfo({ reservation }: { reservation: ReservationRecord }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Activity className="text-muted-foreground h-4 w-4" />
        <span className="text-sm font-medium">Status:</span>
        <ReservationStatus.Root status={reservation.status} />
      </div>
      <div className="flex items-center gap-2">
        <Stethoscope className="text-muted-foreground h-4 w-4" />
        <span className="text-sm font-medium">Speciality:</span>
        <span className="text-sm">{reservation.speciality.name}</span>
      </div>

      <div className="flex items-center gap-2">
        <Calendar className="text-muted-foreground h-4 w-4" />
        <span className="text-sm font-medium">Created:</span>
        <span className="text-sm">
          {formatDateTimeShort(reservation.createdAt)}
        </span>
      </div>
      <AppointmentStatus reservation={reservation} />
      {reservation.doctor && (
        <div className="flex items-center gap-2">
          <User className="text-muted-foreground h-4 w-4" />
          <span className="text-sm font-medium">Assigned Doctor:</span>
          <span className="text-sm">
            {reservation.doctor.firstName} {reservation.doctor.lastName}
          </span>
        </div>
      )}
    </div>
  );
}
